import type { VoiceRole } from './voice-types';

export interface VoiceProfileDefinition {
  id: string;
  defaultLang: string;
  vocabularyHints: string[];
}

export interface ResolvedVoiceProfile {
  id: string;
  lang: string;
  vocabularyHints: string[];
}

const VOICE_PROFILE_DEFINITIONS: Record<string, VoiceProfileDefinition> = {
  default: {
    id: 'default',
    defaultLang: 'zh-CN',
    vocabularyHints: [],
  },
  global_nav: {
    id: 'global_nav',
    defaultLang: 'zh-CN',
    vocabularyHints: [
      'dashboard',
      'chat',
      'transport',
      'haulage',
      'warehouse',
      'accounts',
      'settings',
      'safety',
    ],
  },
  field_dictation: {
    id: 'field_dictation',
    defaultLang: 'zh-CN',
    vocabularyHints: [
      'container',
      'seal',
      'delivery',
      'pickup',
      'dispatcher',
      'arrival',
      'depart',
      'exception',
      'damage',
    ],
  },
  driver_trip_ops: {
    id: 'driver_trip_ops',
    defaultLang: 'zh-CN',
    vocabularyHints: [
      'start trip',
      'end trip',
      'arrive',
      'dispatch',
      'container number',
      'delivery status',
      'park trailer',
      'grounding',
    ],
  },
  warehouse_pick: {
    id: 'warehouse_pick',
    defaultLang: 'zh-CN',
    vocabularyHints: [
      'pick',
      'bin',
      'quantity',
      'confirm',
      'sku',
      'wave',
      'zone',
    ],
  },
  sales_note: {
    id: 'sales_note',
    defaultLang: 'en',
    vocabularyHints: [
      'customer',
      'quotation',
      'follow up',
      'meeting',
      'price',
      'shipment',
    ],
  },
  boss_brief: {
    id: 'boss_brief',
    defaultLang: 'en',
    vocabularyHints: [
      'revenue',
      'margin',
      'delay',
      'exception',
      'customer',
      'today',
    ],
  },
};

const DEFAULT_VOICE_PROFILE: VoiceProfileDefinition = VOICE_PROFILE_DEFINITIONS.default ?? {
  id: 'default',
  defaultLang: 'zh-CN',
  vocabularyHints: [],
};

const ROLE_TO_PROFILE: Partial<Record<VoiceRole, string>> = {
  transport: 'driver_trip_ops',
  haulage: 'driver_trip_ops',
  warehouse: 'warehouse_pick',
  sales: 'sales_note',
  boss: 'boss_brief',
};

function dedupeHints(values: Array<string | undefined | null>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value?.trim();
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }
  return result;
}

export function getVoiceProfileDefinition(profileId?: string | null): VoiceProfileDefinition {
  if (profileId && VOICE_PROFILE_DEFINITIONS[profileId]) {
    return VOICE_PROFILE_DEFINITIONS[profileId];
  }
  return DEFAULT_VOICE_PROFILE;
}

export function resolveVoiceProfile(
  profileId?: string | null,
  options?: {
    role?: VoiceRole;
    lang?: string;
    vocabularyHints?: string[];
  },
): ResolvedVoiceProfile {
  const fallbackProfileId = options?.role ? ROLE_TO_PROFILE[options.role] : undefined;
  const definition = getVoiceProfileDefinition(profileId ?? fallbackProfileId ?? 'default');

  return {
    id: definition.id,
    lang: options?.lang?.trim() || definition.defaultLang,
    vocabularyHints: dedupeHints([
      ...definition.vocabularyHints,
      ...(options?.vocabularyHints ?? []),
    ]),
  };
}
